import React, { useEffect, useState, useRef } from "react";
import { Input, Layout, Button, Result, Form, Spin, Descriptions, Avatar, Image, Card, Divider, Tag, Row, Col, Carousel } from "antd";
import SignaturePad from "../../components/task/SignaturePad";
import { Link, useSearchParams } from "react-router-dom";
import { redirectLogin } from "../../helpers/auth/useSession";

import { getTokenWorklog, signDoc, verifyToken } from "../../api/token";
import { cn } from "../../lib/utils";
import { format } from "date-fns";
import { UserOutlined, ClockCircleOutlined, ToolOutlined, ProjectOutlined, PictureOutlined } from "@ant-design/icons";
import { getCompany } from "../../api/company";
import { getFileUrl } from "../../api/file";

const { Header } = Layout;
const { TextArea } = Input;

export function LogSignView({ hideHeader = false }) {

    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const now = new Date();

    const [form] = Form.useForm();
    const [tokenData, setTokenData] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);
    const signatureRef = useRef();
    const [step, setStep] = useState(1);
    const [isRejected, setIsRejected] = useState(false);

    const [companyLogoUrl, setCompanyLogoUrl] = useState(null);
    const [worklog, setWorklog] = useState(null);
    const [accessories, setAccessories] = useState([]);

    const checkToken = async (token) => {
        try {
            const { results } = await verifyToken(token);
            if (results && results.length) {
                setTokenData(results[0]);
                if (results[0].payload.worklog.accessories) {
                    setAccessories(Object.keys(results[0].payload.worklog.accessories).map(key => ({ name: key, value: results[0].payload.worklog.accessories[key] })));
                }
            }
        } catch (error) {
            console.log("Error verifying token:", error);
        }
    };

    const validateSignature = (rule, value) => {
        if (signatureRef.current.isEmpty()) {
            return Promise.reject("Please sign the document first");
        }
        return Promise.resolve();
    }

    const handlesubmit = async () => {
        try {
            const values = await form.validateFields();

            if (signatureRef.current.isEmpty()) return;

            const signatureDataUrl = signatureRef.current.getSignature();

            setLoading(true);

            const data = new FormData();

            const blob = await fetch(signatureDataUrl).then(res => res.blob());
            data.append('signature', blob, 'signature.png');
            data.append("signatureName", values.signatureName);
            data.append("reason", values.reason);
            data.append("isRejected", isRejected)
            const res = await signDoc(token, data)
            if (res.affected === 1) {
                setSubmitted(true);
            }
        } catch (error) {
            console.log("Error submitting form:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!token) {
            redirectLogin();
            return;
        }

        // Check token validity
        checkToken(token);

        // Fetch company logo
        (async function fetchCompanyLogo() {
            try {
                let company = await getCompany()
                setCompanyLogoUrl(getFileUrl(company.logo))
            }
            catch (error) {
                console.log("Error fetching company logo:", error);
                setCompanyLogoUrl(null)
            }
        })();

        // Fetch worklog with new endpoint
        (async function fetchWorklog() {
            try {
                let worklogData = await getTokenWorklog(token)
                console.log("Worklog data:", worklogData);
                setWorklog(worklogData.worklog);

                // Set accessories if available
                if (worklogData.worklog.accessories) {
                    setAccessories(Object.keys(worklogData.worklog.accessories).map(key => ({
                        name: key,
                        value: worklogData.worklog.accessories[key]
                    })));
                }
            }
            catch (error) {
                console.log("Error fetching worklog:", error);
            }
        })();
    }, [token]);

    // Get formatted worklog details using the new worklog structure
    const worklogItems = worklog ? [
        {
            key: 1,
            label: "Worklog Number",
            children: worklog.worklogNumber
        },
        {
            key: 2,
            label: "Start Time",
            children: format(new Date(worklog.startAt), "yyyy-MM-dd HH:mm:ss")
        },
        {
            key: 3,
            label: "End Time",
            children: format(new Date(worklog.endAt), "yyyy-MM-dd HH:mm:ss"),
            span: 2
        },
        {
            key: 4,
            label: "Work Duration",
            children: worklog.workDuration
        },
        {
            key: 5,
            label: "Break Duration",
            children: worklog.breakDuration,
            span: 2
        },
        {
            key: 6,
            label: "Status",
            children: <Tag color={worklog.status === 'pending' ? 'blue' : worklog.status === 'rejected' ? 'red' : 'green'}>
                {worklog.status.charAt(0).toUpperCase() + worklog.status.slice(1)}
            </Tag>
        }
    ] : [];

    // User details from the worklog data
    const userDetails = worklog && worklog.task && worklog.task.assignee ? [
        {
            key: 'u1',
            label: "Name",
            children: worklog.task.assignee.firstName + ' ' + (worklog.task.assignee.lastName || '')
        }
    ] : [];

    // Equipment details from the worklog data
    const equipmentDetails = worklog && worklog.task && worklog.task.equipment ? [
        {
            key: 'e1',
            label: "Name",
            children: worklog.task.equipment.name
        }
    ] : [];

    // Project details from the worklog data
    const projectDetails = worklog && worklog.task && worklog.task.project ? [
        {
            key: 'p1',
            label: "Name",
            children: worklog.task.project.name
        },
        {
            key: 'p2',
            label: "Description",
            children: worklog.task.project.description
        }
    ] : [];

    return (
        <>
            <div className="flex flex-col items-center justify-start bg-gray-100 min-h-screen">
                {!hideHeader && <Header className="flex items-center justify-between px-[16px] h-[48px] w-full bg-[#f19820]">
                    <h3 className="text-white font-bold text-lg leading-8 flex flex-col justify-center cursor-pointer">
                        TrakHire
                    </h3>
                </Header >}

                <div className="w-full max-w-4xl mt-10 mb-10 px-4">
                    {
                        submitted && <Result
                            status="success"
                            title="Document signed successfully!"
                            subTitle="Thank you for verifying the worklog."
                            extra={[
                                <Link to={"/"} key="home">
                                    <Button type="primary">
                                        Go Home
                                    </Button>
                                </Link>
                            ]}
                        />
                    }

                    {
                        !submitted && (tokenData && new Date(tokenData.expireAt) > now ? (
                            <div className="bg-white rounded-lg shadow-lg overflow-hidden">

                                {/* Header with logo */}
                                <div className="bg-gray-50 p-6 flex items-center justify-between border-b">
                                    <div>
                                        <h1 className="text-2xl font-bold text-gray-800">Worklog Verification</h1>
                                        <p className="text-gray-600">Please review the details and sign the document</p>
                                    </div>
                                    <div className="flex-shrink-0">
                                        {companyLogoUrl ?
                                            <Image
                                                width={120}
                                                src={companyLogoUrl}
                                                alt="Company Logo"
                                                preview={false}
                                                className="object-contain"
                                            /> :
                                            <Avatar shape="square" size={80} icon={<ProjectOutlined />} />
                                        }
                                    </div>
                                </div>

                                <div className="p-6">
                                    {loading ? (
                                        <div className="flex justify-center py-10">
                                            <Spin size="large" />
                                        </div>
                                    ) : (
                                        <>
                                            {/* Project and Task Info */}
                                            {worklog && worklog.task && (
                                                <Card className="mb-6">
                                                    <div className="mb-2">
                                                        <Tag color="blue">Worklog #{worklog.worklogNumber}</Tag>
                                                    </div>
                                                    <h2 className="text-xl font-semibold mb-1">
                                                        <ProjectOutlined className="mr-2" />
                                                        {worklog.task.project.name}
                                                    </h2>
                                                    <p className="text-gray-600">
                                                        <ToolOutlined className="mr-2" />
                                                        Task: {worklog.task.name}
                                                    </p>
                                                    <p className="text-gray-500 mt-2">
                                                        {worklog.task.description}
                                                    </p>
                                                </Card>
                                            )}

                                            <div className={cn(step === 1 ? "" : "hidden")}>
                                                <Row gutter={[24, 24]}>
                                                    {/* User details */}
                                                    <Col xs={24} md={12}>
                                                        <Card title={<><UserOutlined className="mr-2" />User Details</>} bordered={true}>
                                                            {userDetails.length > 0 ?
                                                                <Descriptions column={1} items={userDetails} /> :
                                                                <p>No user details available</p>
                                                            }
                                                        </Card>
                                                    </Col>

                                                    {/* Equipment details */}
                                                    <Col xs={24} md={12}>
                                                        <Card title={<><ToolOutlined className="mr-2" />Equipment Details</>} bordered={true}>
                                                            {equipmentDetails.length > 0 ?
                                                                <Descriptions column={1} items={equipmentDetails} /> :
                                                                <p>No equipment details available</p>
                                                            }
                                                        </Card>
                                                    </Col>

                                                    {/* Project details */}
                                                    <Col xs={24}>
                                                        <Card title={<><ProjectOutlined className="mr-2" />Project Details</>} bordered={true}>
                                                            {projectDetails.length > 0 ?
                                                                <Descriptions column={2} items={projectDetails} /> :
                                                                <p>No project details available</p>
                                                            }
                                                        </Card>
                                                    </Col>
                                                </Row>

                                                {/* Worklog details */}
                                                <Card title={<><ClockCircleOutlined className="mr-2" />Worklog Details</>} className="mt-6" bordered={true}>
                                                    {worklogItems.length > 0 ? (
                                                        <Descriptions layout="vertical" bordered items={worklogItems} />
                                                    ) : (
                                                        <p>No worklog details available</p>
                                                    )}

                                                    {/* Accessories section */}
                                                    {accessories && accessories.length > 0 && (
                                                        <div className="mt-4">
                                                            <Divider orientation="left">Accessories</Divider>
                                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                                {accessories.map((a) => (
                                                                    <div key={a.name} className="bg-gray-50 p-3 rounded-md">
                                                                        <p className="font-semibold">{a.name}:</p>
                                                                        <p>{a.value}</p>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                </Card>

                                                {/* Site Images section */}
                                                {worklog && worklog.siteImages && worklog.siteImages.length > 0 && (
                                                    <Card title={<><PictureOutlined className="mr-2" />Site Images</>} className="mt-6" bordered={true}>
                                                        <Carousel autoplay>
                                                            {worklog.siteImages.map((image, index) => (
                                                                <div key={index} className="h-64 flex items-center justify-center">
                                                                    <Image
                                                                        src={getFileUrl(image)}
                                                                        alt={`Site Image ${index + 1}`}
                                                                        className="max-h-64 object-contain"
                                                                    />
                                                                </div>
                                                            ))}
                                                        </Carousel>
                                                    </Card>
                                                )}

                                                <div className="flex justify-end gap-2 mt-6">
                                                    <Button onClick={() => { setIsRejected(true); setStep(2) }}>Reject</Button>
                                                    <Button type="primary" onClick={() => setStep(2)}>Approve</Button>
                                                </div>
                                            </div>

                                            <Form form={form} layout="vertical" onFinish={handlesubmit} className={cn(step === 1 ? "hidden" : "")}>
                                                <Form.Item name="reason" label="Reason" rules={[{ required: isRejected }]} className={cn(isRejected ? "" : "hidden")}>
                                                    <TextArea placeholder="Please enter your reason for rejecting" />
                                                </Form.Item>

                                                <Form.Item name="signatureName" label="Name" rules={[{ required: true }]}>
                                                    <Input placeholder="Enter your name" />
                                                </Form.Item>

                                                <Form.Item
                                                    label="Signature"
                                                    rules={[{ required: true, validator: validateSignature }]}
                                                    name="signature"
                                                >
                                                    <SignaturePad ref={signatureRef} />
                                                </Form.Item>

                                                <Form.Item>
                                                    <div className="flex gap-2">
                                                        <Button onClick={() => { setStep(1); setIsRejected(false); }}>
                                                            Back
                                                        </Button>
                                                        <Button type="primary" htmlType="submit" disabled={loading} className="flex-grow">
                                                            {loading ? <Spin size="small" /> : "Sign Document"}
                                                        </Button>
                                                    </div>
                                                </Form.Item>
                                            </Form>
                                        </>
                                    )}
                                </div>
                            </div>
                        ) :
                            <Result
                                status="warning"
                                title="Token Expired"
                                subTitle="The Token you submitted has expired. Please check again."
                                extra={[
                                    <Link to={"/"} key="home">
                                        <Button type="primary">
                                            Go Home
                                        </Button>
                                    </Link>
                                ]}
                            />
                        )
                    }

                    {
                        !tokenData &&
                        <Result
                            status="error"
                            title="Invalid Token"
                            subTitle="The Token you submitted is invalid. Please check again."
                            extra={[
                                <Link to={"/"} key="home">
                                    <Button type="primary">
                                        Go Home
                                    </Button>
                                </Link>
                            ]}
                        />
                    }
                </div>
            </div>
        </>
    )
}